import React, { useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import { Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { CreateOrgUserOut, OrgRole, UserService } from '../api';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { useLanguage } from '../common/GeneralUtils';
import { getActiveLocations, isMultiLocation } from '../common/filter/locationGrouping';
import { UserLocationSelect } from './UserLocationSelect';
import { ASSIGNABLE_ROLES, apiErrorDetail, isReservedEmail } from './userManagementUtils';

const EMAIL_EXISTS_DETAIL = 'email_exists';
const LOCATION_NOT_FOUND_DETAIL = 'location_not_found';

type CreateUserDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (result: CreateOrgUserOut) => void;
};

type CreateUserFormValues = {
  email: string;
  first_name: string;
  last_name: string;
  role: OrgRole;
  location_id: string | null;
  is_floating: boolean;
};

export const CreateUserDialog = ({ isOpen, onClose, onCreated }: CreateUserDialogProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const language = useLanguage();
  const { account } = useGetAccountAuthenticated({});
  const [emailExists, setEmailExists] = useState(false);

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .required(t('users.errors.required'))
      .email(t('users.errors.invalid_email'))
      .test('reserved-email', t('users.errors.reserved_email'), (email) => !isReservedEmail(email)),
    first_name: yup.string().trim().required(t('users.errors.required')),
    last_name: yup.string().trim().required(t('users.errors.required')),
    role: yup.string().required(t('users.errors.required')),
  });

  const activityLabelPlural = account?.activity_label_plural?.[language] ?? t('passes.activity_fallback_plural');
  const shouldShowLocations = !!account && isMultiLocation(account);
  const activeLocations = account ? getActiveLocations(account) : [];
  const defaultLocationId = (activeLocations.find((location) => location.default) ?? activeLocations[0])?.id ?? null;

  const initialValues: CreateUserFormValues = {
    email: '',
    first_name: '',
    last_name: '',
    role: OrgRole.EMPLOYEE,
    location_id: shouldShowLocations ? defaultLocationId : null,
    is_floating: false,
  };

  const onSubmit = async (values: CreateUserFormValues, { setFieldError }: FormikHelpers<CreateUserFormValues>) => {
    setEmailExists(false);
    try {
      const result = await safeExecute({
        apiCall: () =>
          UserService.createOrgUser({
            email: values.email.trim(),
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            role: values.role,
            location_id: values.location_id ?? undefined,
            is_floating: values.is_floating,
          }),
        successMessage: '',
        suppressSuccessMessage: true,
        onErrorMessageGenerator: (error) => {
          const detail = apiErrorDetail(error);
          if (detail === EMAIL_EXISTS_DETAIL) {
            return t('users.errors.email_exists');
          }
          if (detail === LOCATION_NOT_FOUND_DETAIL) {
            return t('users.errors.location_not_found');
          }
          return error.body?.message || t('api.error.default');
        },
      });
      onCreated(result);
    } catch (error) {
      if (apiErrorDetail(error) === EMAIL_EXISTS_DETAIL) {
        setEmailExists(true);
        setFieldError('email', t('users.errors.email_exists_field'));
      }
      if (apiErrorDetail(error) === LOCATION_NOT_FOUND_DETAIL) {
        setFieldError('location_id', t('users.errors.location_not_found'));
      }
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('users.create.title')}</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setValues }) => (
          <Form noValidate>
            <DialogContent>
              <Grid container flexDirection="column" gap={2}>
                {emailExists && <Alert severity="error">{t('users.errors.email_exists')}</Alert>}
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  label={t('users.fields.email')}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && !!errors.email}
                  helperText={touched.email ? errors.email : undefined}
                  inputProps={{ dir: 'ltr' }}
                />
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      name="first_name"
                      label={t('order_page.firstname')}
                      value={values.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.first_name && !!errors.first_name}
                      helperText={touched.first_name ? errors.first_name : undefined}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      name="last_name"
                      label={t('order_page.lastname')}
                      value={values.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.last_name && !!errors.last_name}
                      helperText={touched.last_name ? errors.last_name : undefined}
                    />
                  </Grid>
                </Grid>
                <TextField
                  select
                  fullWidth
                  size="small"
                  name="role"
                  label={t('users.fields.role')}
                  value={values.role}
                  onChange={handleChange}
                  helperText={t('users.role_help')}>
                  {ASSIGNABLE_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {t(`users.roles.${role}`)}
                    </MenuItem>
                  ))}
                </TextField>
                {shouldShowLocations && (
                  <UserLocationSelect
                    value={{ locationId: values.location_id, isFloating: values.is_floating }}
                    onChange={({ locationId, isFloating }) =>
                      setValues({ ...values, location_id: locationId, is_floating: isFloating })
                    }
                    error={!!errors.location_id}
                    helperText={
                      errors.location_id ?? t('users.location_help_create', { activityPlural: activityLabelPlural })
                    }
                  />
                )}
                <Alert severity="info">{t('users.create.info')}</Alert>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button sx={{ textTransform: 'none' }} onClick={onClose}>
                {t('cancel')}
              </Button>
              <LoadingButton loading={isSubmitting} type="submit" variant="contained" sx={{ textTransform: 'none' }}>
                {t('users.create.submit')}
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
